(() => {
  if (globalThis.RicochetDiscoveryAnalytics) return;
  const config = document.querySelector('[data-discovery-analytics]');
  const enabled = config?.dataset.enabled === 'true';
  const role = globalThis.Shopify?.theme?.role || config?.dataset.themeRole || 'unknown';
  const themeId = String(globalThis.Shopify?.theme?.id || config?.dataset.themeId || '');
  const preview = role !== 'main' || config?.dataset.designMode === 'true' || Boolean(globalThis.Shopify?.designMode)
    || new URLSearchParams(location.search).has('preview_theme_id')
    || location.hostname.endsWith('.shopifypreview.com');
  const debug = preview && role !== 'main'
    && new URLSearchParams(location.search).get('discovery_debug') === '1';
  // Owner-approved receiver proof, not a general preview or consent bypass.
  const receiverTest = themeId === '165299159075' && role === 'unpublished'
    && location.hostname === 'ricochetoffroad.com'
    && config?.dataset.designMode !== 'true' && !globalThis.Shopify?.designMode
    && new URLSearchParams(location.search).get('discovery_test') === '1';
  const names = new Set(['search_submitted', 'search_results', 'search_selected',
    'navigation_filtered', 'navigation_selected', 'discovery_recovery', 'search_failed']);
  const surfaces = new Set(['predictive', 'search_page', 'navigation_page', 'header_navigation']);
  const modes = new Set(['collection', 'product', 'native']);
  const actions = new Set(['clear', 'show_more', 'full_site', 'switch_mode', 'search_all']);
  const state = new WeakMap();
  const timers = new Set();
  const count = (value) => Math.max(0, Math.min(100000, Math.floor(Number(value) || 0)));
  const allowed = () => {
    try { return globalThis.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  const path = (value) => {
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin && /^\/(collections|products|pages)\/[a-z0-9][a-z0-9-]*\/?$/.test(url.pathname)
        ? url.pathname : '';
    } catch { return ''; }
  };
  const diagnostic = (name, payload, status) => {
    if (debug) console.info(`[Ricochet discovery] ${JSON.stringify({ event: name, ...payload, consent_allowed: allowed(), transport: status })}`);
  };
  const emit = (name, input = {}) => {
    try {
      if (!enabled || !names.has(name) || !surfaces.has(input.surface)) return false;
      const query = String(input.query || '');
      const normalized = globalThis.RicochetSearchNormalizer?.normalizeSearchQuery?.(query) || query;
      const payload = {
        schema_version: 1, surface: input.surface,
        placement: input.placement === 'header' || input.placement === 'page' ? input.placement
          : input.surface === 'header_navigation' ? 'header' : 'page',
        source_path: path(location.href) || (location.pathname === '/search' ? '/search' : location.pathname === '/' ? '/' : ''),
        theme_id: /^\d+$/.test(themeId) ? themeId : '',
        theme_role: role === 'main' ? 'main' : 'preview',
        query_length: Math.min(200, query.length), normalized: normalized !== query
      };
      if (modes.has(input.mode)) payload.mode = input.mode;
      for (const [from, to] of [['total', 'result_count'], ['shown', 'shown_count'], ['rank', 'rank']]) {
        if (input[from] !== undefined) payload[to] = count(input[from]);
      }
      const destination = input.destination && path(input.destination);
      if (destination) payload.destination = destination;
      if (actions.has(input.action)) payload.action = input.action;
      if (preview && !receiverTest) { diagnostic(`ricochet:${name}`, payload, 'local_only'); return debug; }
      if (receiverTest) payload.test_mode = true;
      if (!allowed() || typeof globalThis.Shopify?.analytics?.publish !== 'function') return false;
      const result = globalThis.Shopify.analytics.publish(`ricochet:${name}`, payload);
      if (receiverTest) diagnostic(`ricochet:${name}`, payload, 'test_publish_attempt');
      // This is a publish attempt, not receipt. Never wait, retry or replay on failure.
      if (result?.catch) result.catch(() => {});
      return result !== false;
    } catch { return false; } // Measurement must not break shopping.
  };
  const cancel = (owner) => {
    const current = state.get(owner);
    if (current?.timer) { clearTimeout(current.timer); timers.delete(current.timer); current.timer = null; }
  };
  const settled = (name, surface, owner, context) => {
    cancel(owner);
    if (!enabled || (!debug && ((preview && !receiverTest) || !allowed()))) return;
    const current = state.get(owner) || {};
    // Query stays in an ephemeral dedupe key only, never in customData or logs.
    const key = JSON.stringify([name, surface, context.query, context.mode, context.total, context.shown]);
    if (current.key === key) return;
    const timer = setTimeout(() => {
      deliver();
    }, 500);
    const deliver = () => {
      timers.delete(timer);
      current.timer = null;
      current.deliver = null;
      if (!owner.isConnected) return;
      if (emit(name, { ...context, surface })) current.key = key;
    };
    current.deliver = deliver;
    current.timer = timer;
    timers.add(timer);
    state.set(owner, current);
  };
  const flush = (owner) => {
    const current = state.get(owner);
    if (current?.timer && timers.has(current.timer)) {
      clearTimeout(current.timer);
      current.deliver();
    }
  };
  const placementFor = (owner) => owner?.querySelector('.js-search-input')?.id === 'header-search' ? 'header' : 'page';
  const results = (surface, owner, context) => settled('search_results', surface, owner, { ...context, placement: placementFor(owner) });
  const navigation = (owner, context) => settled('navigation_filtered', 'navigation_page', owner, context);
  const queryFor = (owner) => owner?.querySelector('input[name="original_q"]')?.value
    || owner?.querySelector('.js-search-input, [data-navigation-filter], input[name="q"]')?.value
    || (owner?.tagName === 'RICOCHET-SEARCH-RESULTS' || owner?.hasAttribute('data-native-search-results')
      ? new URLSearchParams(location.search).get('original_q') || new URLSearchParams(location.search).get('q') : '')
    || owner?.dataset.query || '';
  const rankFor = (link) => {
    const item = link.closest('li');
    return item ? [...item.parentElement.children].indexOf(item) + 1 : 0;
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.defaultPrevented || !form.matches?.('form.search')) return;
    const query = queryFor(form);
    if (!query.trim()) return;
    emit('search_submitted', { surface: form.closest('predictive-search') ? 'predictive' : 'search_page', query, placement: placementFor(form) });
  });
  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('a, button');
    if (!target) return;
    const predictive = target.closest('predictive-search');
    const search = target.closest('ricochet-search-results, [data-native-search-results]');
    const nav = target.closest('[data-ricochet-navigation-page]');
    const surface = predictive ? 'predictive' : search ? 'search_page' : nav ? 'navigation_page' : 'header_navigation';
    const owner = predictive || search || nav;
    const placement = owner ? placementFor(owner) : 'header';
    const query = queryFor(owner);
    const recovery = target.matches('.js-search-reset, [data-navigation-clear]') ? 'clear'
      : target.matches('[data-search-show-more]') ? 'show_more'
        : target.matches('[data-search-standard]') ? 'full_site'
          : target.matches('[data-search-products]') ? 'switch_mode'
            : target.closest('[data-navigation-help]') ? 'search_all' : '';
    if (recovery) {
      if (recovery === 'clear' && owner) cancel(owner);
      emit('discovery_recovery', { surface, query, placement, action: recovery });
      return;
    }
    if (!target.matches('a[href]') || !path(target.href)) return;
    if (predictive || search) {
      // A fast selection completes the interaction before the idle timer. Count
      // its presented outcome too, without replaying canceled/denied activity.
      flush(predictive || document.querySelector('ricochet-search-results') || search);
      emit('search_selected', { surface, query, placement, rank: rankFor(target), destination: target.href,
        mode: target.pathname.startsWith('/collections/') ? 'collection'
          : target.pathname.startsWith('/products/') ? 'product' : 'native' });
    } else if (nav || target.closest('header, nav, .header')) {
      if (nav) flush(nav);
      emit('navigation_selected', { surface, query, placement, rank: rankFor(target), destination: target.href });
    }
  }, true);
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { timers.forEach(clearTimeout); timers.clear(); }
  });
  // Documented Shopify API, never cookie-based inference or consent modification.
  try {
    if (enabled && !globalThis.Shopify?.customerPrivacy && globalThis.Shopify?.loadFeatures) {
      globalThis.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
    }
  } catch { /* Fail closed if privacy loading is unavailable. */ }
  globalThis.RicochetDiscoveryAnalytics = { emit, results, navigation, cancel };
})();
